import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IPerformancePeriodQuery } from 'app/library/performance-period/interfaces/performance-period.query.interface';
import { PerformancePeriod } from 'app/library/performance-period/models/performance-period.model';
import { TenantDashboardChartId } from 'app/library/tenant-dashboard/enums/tenant-dashboard-chart-id.enum';
import { TenantDashboardChartType } from 'app/library/tenant-dashboard/enums/tenant-dashboard-chart-type.enum';
import { ITenantDashboardQuery } from 'app/library/tenant-dashboard/interfaces/tenant-dashboard.query.interface';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IEntityApiService } from 'app/projects/entity/src/lib/interfaces/entity.service.interface';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { ISimpleEntityQuery } from 'app/projects/shared/src/lib/interfaces/simple-entity.query.interface';
import { SimpleEntity } from 'app/projects/shared/src/lib/models/simple-entity.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IPerformanceTeamReviewActionPayload } from '../interfaces/create-performance-team-review.interface';
import { IPerformanceTeamMemberOverviewQuery } from '../interfaces/performance-team-member-overview.query.interface';
import { IPerformanceTeamOverviewQuery } from '../interfaces/performance-team-overview.query.interface';
import { IPerformanceTeamReviewQuery } from '../interfaces/performance-team-review.query.interface';
import { PerformanceTeamMemberOverview } from '../models/performance-team-member-overview.model';
import { PerformanceTeamOverview } from '../models/performance-team-overview.model';
import { PerformanceTeamReviewCommand } from '../models/performance-team-review.command.model';
import { PerformanceTeamReview } from '../models/performance-team-review.model';

export interface IPerformanceTeamReviewsAndTotalCount {
    data: IPerformanceTeamReviewQuery[];
    pagination: {
        number_of_pages: number;
        number_of_results: number;
    };
}

@Injectable()
export class PerformanceTeamReviewApiService extends EntityService<IPerformanceTeamReviewQuery, PerformanceTeamReviewCommand> implements IEntityApiService<PerformanceTeamReview> {
    chartOptionsAxisTitle = {
        display: true,
        color: '#464B54',
        font: {
            family: 'Roboto',
            size: 11,
            weight: 600,
        },
    };

    chartScaleNoAxis = {
        grid: {
            drawBorder: false,
        },
    };

    chartScaleNoGrid = {
        grid: {
            display: false,
        },
    };

    chartPluginTooltip = {
        tooltip: {
            callbacks: {
                label: function (context) {
                    return context.formattedValue;
                },
            },
        },
    };

    barChartOptions = {
        elements: {
            bar: {
                borderRadius: {
                    topLeft: 10,
                    topRight: 10,
                    bottomLeft: 10, // Rounded corners for bottom
                    bottomRight: 10, // Rounded corners for bottom
                },
            },
        },
        plugins: {
            ...this.chartPluginTooltip,
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                ...this.chartScaleNoGrid,
                beginAtZero: false,
                ticks: {
                    font: {
                        size: 9, // Font size for x axis labels
                    },
                    maxRotation: 0, // No rotation
                    minRotation: 0, // No rotation
                },
            },
            y: {
                display: false,
                max: 10,
            },
        },
        colors: ['#F3AD3E', '#578DBF', '#C5639C', '#95AF3F', '#B91C1C'],
        bgColors: ['#FBE78D', '#A3CAEA ', '#F0BFDF', '#D5E1A4', '#FECACA'],
    };

    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment, private _translateService: TranslateService) {
        super(http, environment.apiUrl, 'team-reviews');
    }

    getAll(): Observable<PerformanceTeamReview[]> {
        return this._getAll().pipe(map((queries) => queries.map((query) => new PerformanceTeamReview(query))));
    }

    create(entity: PerformanceTeamReview): Observable<PerformanceTeamReview> {
        const data = new PerformanceTeamReviewCommand(entity);

        return this._create(data).pipe(map((query) => new PerformanceTeamReview(query)));
    }

    read(id: string): Observable<PerformanceTeamReview> {
        return this._read(id).pipe(
            map((query) => {
                const mappedCharts = this.getFullChartData(query);
                return new PerformanceTeamReview({ ...query, charts: mappedCharts });
            })
        );
    }

    update(entity: PerformanceTeamReview): Observable<PerformanceTeamReview> {
        const data = new PerformanceTeamReviewCommand(entity);

        return this.http.patch<IPerformanceTeamReviewQuery>(`${this.apiUrl}/${this.entities}/${entity.id}`, data).pipe(map((query) => new PerformanceTeamReview(query)));
    }

    delete(id: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/${this.entities}/${id}`);
    }

    getPerformanceTeamReviews(
        teamId: string,
        pageSize = 10,
        pageIndex = 1,
        search = '',
        type = '',
        periodUnderReview = '',
        goalSettingPeriod = '',
        userId = ''
    ): Observable<IPerformanceTeamReviewsAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (type) {
            params = params.set('filter[type]', type);
        }

        if (periodUnderReview) {
            params = params.set('filter[periodUnderReview]', periodUnderReview);
        }

        if (goalSettingPeriod) {
            params = params.set('filter[goalSettingPeriod]', goalSettingPeriod);
        }

        if (userId) {
            params = params.set('filter[userId]', userId);
        }

        return this.http.get<IPerformanceTeamReviewsAndTotalCount>(`${this.apiUrl}/teams/${teamId}/${this.entities}`, { params });
    }

    createPerformanceTeamReview(entity: IPerformanceTeamReviewActionPayload): Observable<PerformanceTeamReview> {
        const data = new PerformanceTeamReviewCommand(entity);

        return this.http.post<IPerformanceTeamReviewQuery>(`${this.apiUrl}/${this.entities}`, data).pipe(map((query) => new PerformanceTeamReview(query)));
    }

    updatePerformanceTeamParticipants(teamReviewId: string, userIds: string[]): Observable<PerformanceTeamReview> {
        const data = {
            user_ids: userIds,
        };
        return this.http.put<IPerformanceTeamReviewQuery>(`${this.apiUrl}/${this.entities}/${teamReviewId}/set-users`, data).pipe(map((query) => new PerformanceTeamReview(query)));
    }

    updatePerformanceTeamReview(performanceTeamReviewId: string, entity: IPerformanceTeamReviewActionPayload): Observable<PerformanceTeamReview> {
        const data = {
            name: entity.name,
            start_date: entity.startDate,
            due_date: entity.dueDate,
        };

        return this.http
            .put<IPerformanceTeamReviewQuery>(`${this.apiUrl}/${this.entities}/${performanceTeamReviewId}`, data)
            .pipe(map((query) => new PerformanceTeamReview(query)));
    }

    getPerformanceTeamMemberOverview(teamId: string, userId: string): Observable<PerformanceTeamMemberOverview[]> {
        return this.http
            .get<IPerformanceTeamMemberOverviewQuery[]>(`${this.apiUrl}/teams/${teamId}/users/${userId}/performance-overview`)
            .pipe(map((queries) => queries.map((query) => new PerformanceTeamMemberOverview(query))));
    }

    getPerformanceTeamOverview(teamId: string): Observable<PerformanceTeamOverview> {
        return this.http.get<IPerformanceTeamOverviewQuery>(`${this.apiUrl}/teams/${teamId}/performance-overview`).pipe(map((query) => new PerformanceTeamOverview(query)));
    }

    getAvailablePerformanceTemplateTypesTeams(teamId: string): Observable<SimpleEntity[]> {
        return this.http
            .get<ISimpleEntityQuery[]>(`${this.environment.apiUrl}/teams/${teamId}/available-template-types`)
            .pipe(map((queries) => queries.map((query) => new SimpleEntity(query))));
    }

    getPerformanceTeamPeriods(teamId: string): Observable<PerformancePeriod[]> {
        return this.http
            .get<IPerformancePeriodQuery[]>(`${this.environment.apiUrl}/teams/${teamId}/team-reviews/periods-under-review`)
            .pipe(map((queries) => queries.map((query) => new PerformancePeriod(query))));
    }

    getPerformanceTemplateTypesTeams(teamId: string): Observable<SimpleEntity[]> {
        return this.http
            .get<ISimpleEntityQuery[]>(`${this.environment.apiUrl}/teams/${teamId}/team-reviews/template-types`)
            .pipe(map((queries) => queries.map((query) => new SimpleEntity(query))));
    }

    getFullChartData(query: IPerformanceTeamReviewQuery): ITenantDashboardQuery[] {
        return query.charts.map((chart) => {
            let updatedChart = null;

            switch (chart.chart_id) {
                case TenantDashboardChartId.PersonalReviewCompletionState:
                    chart.chart_data.labels = chart.chart_data.labels.map((label) => this._translateService.instant('PERFORMANCE_REVIEW.STATUS.' + label.toUpperCase()));

                    updatedChart = Object.assign({}, chart, {
                        type: TenantDashboardChartType.Bar,
                        options: {
                            ...this.barChartOptions,
                        },
                    });

                    break;

                default:
                    break;
            }

            if (!updatedChart.aggregates) {
                updatedChart.chart_data = {
                    ...updatedChart.chart_data,
                    data_series: updatedChart.chart_data.data_series.map((dataset) => {
                        return {
                            ...dataset,
                            color: updatedChart.options.colors,
                            hoverBackgroundColor: updatedChart.options.bgColors,
                            hoverBorderColor: updatedChart.options.colors,
                        };
                    }),
                };
            } else {
                updatedChart.metric_color = updatedChart.options.colors[0];
            }

            return updatedChart;
        });
    }
}
